import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Info, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SchemaMarkup, generateBreadcrumbSchema } from "@/components/SchemaMarkup";
import { AppStoreCTA } from "@/components/AppStoreCTA";
import { supabase } from "@/integrations/supabase/client";
import { airports } from "@/data/airports";
import NotFound from "./NotFound";

interface GuidePage {
  slug: string;
  kind: string;
  airport_code: string | null;
  title: string;
  description: string;
  body: string;
  source_note: string | null;
  published_at: string;
  updated_at: string;
}

const GuidePost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<GuidePage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    supabase
      .from("content_pages")
      .select("slug, kind, airport_code, title, description, body, source_note, published_at, updated_at")
      .eq("slug", slug ?? "")
      .eq("status", "published")
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setPage((data as GuidePage) ?? null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading guide…
      </div>
    );
  }

  if (!page) return <NotFound />;

  const url = `https://airportbuddy.app/guides/${page.slug}`;
  const airport = page.airport_code ? airports.find((a) => a.code === page.airport_code) : undefined;
  const updated = new Date(page.updated_at).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <>
      <Helmet>
        <title>{page.title.slice(0, 60)}</title>
        <meta name="description" content={page.description.slice(0, 158)} />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <SchemaMarkup
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: page.title,
          description: page.description,
          datePublished: page.published_at,
          dateModified: page.updated_at,
          mainEntityOfPage: url,
          publisher: { "@type": "Organization", name: "Airport Buddy" },
        }}
      />
      <SchemaMarkup
        schema={generateBreadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "Guides", url: "/guides" },
          { name: page.title, url: `/guides/${page.slug}` },
        ])}
      />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 py-8">
          <Button asChild variant="ghost" className="mb-6 min-h-[44px]">
            <Link to="/guides">
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              All guides
            </Link>
          </Button>

          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Guides", href: "/guides" },
              { label: page.title, href: `/guides/${page.slug}` },
            ]}
          />

          <article className="max-w-3xl mx-auto space-y-6">
            <header className="space-y-4">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">{page.title}</h1>
              <div className="inline-flex items-center gap-2 rounded-full bg-green-500/10 px-3 py-1 text-sm font-medium text-green-600 dark:text-green-400">
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                Updated {updated}
              </div>
            </header>

            <Card className="shadow-md rounded-xl">
              <CardContent className="pt-6 prose prose-base md:prose-lg max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary prose-strong:text-foreground">
                <ReactMarkdown>{page.body}</ReactMarkdown>
              </CardContent>
            </Card>

            {page.source_note && (
              <Card className="rounded-xl border-dashed">
                <CardContent className="pt-6 flex gap-3 text-base text-muted-foreground">
                  <Info className="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" />
                  <p>
                    <span className="font-medium text-foreground">Where this comes from: </span>
                    {page.source_note}
                  </p>
                </CardContent>
              </Card>
            )}

            {airport && (
              <Card className="rounded-xl">
                <CardContent className="pt-6 space-y-3">
                  <h2 className="font-bold text-foreground">Check it live before you go</h2>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/airports/${airport.code.toLowerCase()}`}
                      className="inline-flex min-h-[44px] items-center rounded-xl border border-border px-4 text-base text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 active:scale-95"
                    >
                      {airport.code} security wait right now
                    </Link>
                    <Link
                      to={`/airports/${airport.code.toLowerCase()}/terminals`}
                      className="inline-flex min-h-[44px] items-center rounded-xl border border-border px-4 text-base text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 active:scale-95"
                    >
                      {airport.code} terminals
                    </Link>
                    <Link
                      to={`/airports/${airport.code.toLowerCase()}/parking`}
                      className="inline-flex min-h-[44px] items-center rounded-xl border border-border px-4 text-base text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 active:scale-95"
                    >
                      {airport.code} parking
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            <AppStoreCTA
              placement="guide_post"
              context={page.slug}
              headline="Know when to leave, without checking"
              subline="The free Airport Buddy iPhone app watches the security line for you."
            />
          </article>
        </div>
      </div>
    </>
  );
};

export default GuidePost;
